import { useEffect } from 'react';

import { API } from '@/shared/api';
import { IS_WHITE_LABEL } from '@/shared/environments';
import { pollLinkedMediaUploadStatus } from '@/shared/lib/pollLinkedMediaUploadStatus';

import { consumeLandingFlow, peekLandingFlow, saveLandingProgressFloor } from './landingFlowStorage';
import { type DubLandingFlowData, LandingFlowKind } from './types';
import { useLandingProjectStore } from './useLandingProjectStore';

const resolveMediaLink = async (data: DubLandingFlowData): Promise<string> => {
  if ('media_link' in data) return data.media_link;

  const landingMedia = await API.getLandingMedia(data.media_id);
  return landingMedia.url;
};

const createLandingProject = async (data: DubLandingFlowData, spaceId: string): Promise<void> => {
  const store = useLandingProjectStore.getState();
  store.start(spaceId);

  try {
    const mediaLink = await resolveMediaLink(data);

    store.setProgress(10);
    const libraryMedia = await API.uploadVideoByLink({ link: mediaLink });

    store.setProgress(14);
    const status = await pollLinkedMediaUploadStatus(libraryMedia.id, {
      onStatusCheckError: (error) => console.error('Error checking upload status:', error),
    });
    if (status !== 'ready') {
      throw new Error(`Media upload ${status}`);
    }

    store.setProgress(18);
    const project = await API.createProject({
      name: '',
      dst_lang: data.target_lang,
      prompt: null,
      space_id: spaceId,
      video_id: libraryMedia.id,
    });

    saveLandingProgressFloor(20);
    store.setReady(project.id);
  } catch (error) {
    store.setFailed(error);
  }
};

type UseStartLandingProjectParams = {
  defaultSpaceId?: string;
  isAuthenticated: boolean;
};

export const useStartLandingProject = ({ defaultSpaceId, isAuthenticated }: UseStartLandingProjectParams): void => {
  useEffect(() => {
    if (IS_WHITE_LABEL || !isAuthenticated || !defaultSpaceId) return;
    if (useLandingProjectStore.getState().status !== 'idle') return;

    const data = peekLandingFlow();
    if (!data || data.kind !== LandingFlowKind.videoTranscript) return;

    consumeLandingFlow();
    void createLandingProject(data, defaultSpaceId);
  }, [isAuthenticated, defaultSpaceId]);
};
